import InlineManualEntryModal from "#components/ManualEntry/ManualEntryForms/Inline/InlineManualEntryModal";
import useInlineManualEntryModal from "#components/ManualEntry/ManualEntryForms/useManualEntryModal";
import { useTestDetail } from "#hooks/useTests";
import { ensureStreamListIsFetched } from "#redux/actions/index";
import { getBreadcrumbsObject } from "#routers/breadcrumbsHelper";
import SampleService from "#services/SampleService";
import { Button, Page, useToast } from "@validereinc/common-components";
import classNames from "classnames/bind";
import { find } from "es-toolkit/compat";
import * as PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import DownloadableReportsPanel from "./DownloadableReportsPanel";
import InstrumentWarningPanel from "./InstrumentWarningPanel";
import NotesPanel from "./NotesPanel/NotesPanel";
import styles from "./TestDetail.module.scss";
import TestDetailSummary from "./TestDetailSummary";
import TestMeasurementTable from "./TestMeasurementTable";
import { getMeasurementDisplayProperties } from "./TestMeasurementTableHelper";

const cx = classNames.bind(styles);

const mapDispatchToProps = {
  ensureStreamListIsFetched,
};

const mapStateToProps = ({ streams }) => ({
  streams: streams?.data?.toJS() ?? [],
});

const TestDetail = ({
  match,
  breadcrumbs,
  streams,
  ensureStreamListIsFetched,
}) => {
  const [testDetail, testDetailLoadingState, refetchTest] = useTestDetail(
    match.params.testId
  );
  const [loadingState, setLoadingState] = useState("enabled");
  const { toast } = useToast();

  function onMarkError() {
    setLoadingState("loading");

    // @ts-expect-error legacy opshub typing
    SampleService.markTestAsError(testDetail?.id, "")
      .then(() => {
        toast.push({
          intent: "success",
          description: "The test has been marked as an error.",
        });
        refetchTest();
      })
      .finally(() => {
        setLoadingState("enabled");
      });
  }

  const stream = useMemo(
    // @ts-expect-error legacy opshub typing
    () => find(streams, { id: testDetail?.sample?.stream_id }),
    [testDetail]
  );

  const [showInline, showInlineModal, onHideInlineModal] =
    useInlineManualEntryModal();

  const pageBreadcrumbs = useMemo(() => {
    const pageBreadcrumbs = getBreadcrumbsObject(breadcrumbs, match.params);

    if (pageBreadcrumbs[2] && stream?.name) {
      pageBreadcrumbs[2] = {
        ...pageBreadcrumbs[2],
        title: stream?.name,
      };
    }

    return pageBreadcrumbs;
  }, [breadcrumbs, match.params, stream]);

  const [displayProperties, displayPropertiesWithAlerts] = useMemo(() => {
    return getMeasurementDisplayProperties(testDetail);
  }, [testDetail]);

  useEffect(() => {
    ensureStreamListIsFetched();
  }, []);

  const actionRow = (
    <div className={cx("actionsRow")}>
      {/* @ts-expect-error legacy opshub typing */}
      {testDetail?.status !== "error" && (
        <Button
          variant="error-outline"
          onClick={onMarkError}
          isLoading={loadingState === "loading"}
          disabled={loadingState !== "enabled"}
        >
          Mark as Error
        </Button>
      )}

      <Button
        onClick={showInlineModal}
        variant="primary"
      >
        Add Inline Measurement
      </Button>
    </div>
  );

  // @ts-expect-error legacy opshub typing
  const pageTitle = testDetail?.name
    ? // @ts-expect-error legacy opshub typing
      `${testDetail?.name} Test`
    : "Test Detail";

  return (
    <Page
      title={pageTitle}
      breadcrumbs={pageBreadcrumbs}
      isLoading={testDetailLoadingState === "loading"}
      actionRow={actionRow}
    >
      <TestDetailSummary
        testDetail={testDetail}
        testDetailLoadingState={testDetailLoadingState}
      />

      <TestMeasurementTable
        // @ts-expect-error legacy opshub typing
        testDetail={testDetail}
        displayProperties={displayPropertiesWithAlerts}
        title="Alerted Measurements"
      />
      <TestMeasurementTable
        // @ts-expect-error legacy opshub typing
        testDetail={testDetail}
        displayProperties={displayProperties}
        title="All Measurements"
      />
      <DownloadableReportsPanel
        // @ts-expect-error legacy opshub typing
        sampleId={testDetail?.sample_id}
        // @ts-expect-error legacy opshub typing
        pdfReportLink={testDetail?.pdf_url}
        // @ts-expect-error legacy opshub typing
        labReportId={testDetail?.lab_report_id}
        // @ts-expect-error legacy opshub typing
        instrumentType={testDetail?.instrument?.type}
        // @ts-expect-error legacy opshub typing
        testImageUrls={testDetail?.test_image_urls}
      />

      <InlineManualEntryModal
        show={showInline}
        onHide={onHideInlineModal}
        testInfo={testDetail}
        refetchData={refetchTest}
      />

      <InstrumentWarningPanel
        // @ts-expect-error legacy opshub typing
        metadata={testDetail?.metadata}
        // @ts-expect-error legacy opshub typing
        labels={testDetail?.labels}
      />

      <NotesPanel
        // @ts-expect-error legacy opshub typing
        testId={testDetail?.id}
        refreshTest={refetchTest}
      />
    </Page>
  );
};

TestDetail.propTypes = {
  height: PropTypes.number.isRequired,
  match: PropTypes.object.isRequired,
  breadcrumbs: PropTypes.array.isRequired,
  streams: PropTypes.array.isRequired,
  ensureStreamListIsFetched: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(TestDetail);
